/*
 * SacredEmblem — the Agent Eros logo cluster: aura + rings + particles +
 * seed-of-life geometry + colour-shifting logo, sized as a single unit.
 *
 * Uses canvas 2D for particles and geometry (not full 5-layer footer effect,
 * but a matched-DNA compact version). Renders at any size via the `size` prop.
 */
import React, { useEffect, useRef } from "react";
import "./SacredEmblem.css";

export const SacredEmblem = ({ size = 170, className = "" }) => {
  const particlesRef = useRef(null);
  const geometryRef  = useRef(null);
  const rafRef = useRef(null);
  const t0Ref  = useRef(0);

  useEffect(() => {
    t0Ref.current = performance.now();
    const isCyber = typeof document !== "undefined" &&
      document.documentElement.getAttribute("data-theme") === "agent_eros_1";
    const pColors = isCyber
      ? [
          "rgba(168, 85, 247, .85)",
          "rgba(236, 72, 153, .75)",
          "rgba(34, 211, 238, .60)",
        ]
      : [
          "rgba(212,175,106,.85)",
          "rgba(139,20,45,.75)",
          "rgba(233,226,216,.6)",
        ];
    const geoStroke = isCyber ? "rgba(168, 85, 247, .32)" : "rgba(212,175,106,.28)";
    const particles = Array.from({ length: 30 }, (_, i) => ({
      a: Math.random() * Math.PI * 2,
      r: 60 + Math.random() * 50,
      speed: 0.1 + Math.random() * 0.25,
      size: 1 + Math.random() * 2,
      color: pColors[i % pColors.length],
      phase: Math.random() * Math.PI * 2,
    }));

    const drawFrame = (now) => {
      const t = (now - t0Ref.current) / 1000;
      const pc = particlesRef.current;
      const gc = geometryRef.current;

      if (pc) {
        const ctx = pc.getContext("2d");
        ctx.clearRect(0, 0, pc.width, pc.height);
        const cx = pc.width / 2;
        const cy = pc.height / 2;
        particles.forEach((p) => {
          const ang = p.a + t * p.speed;
          const x = cx + Math.cos(ang) * p.r;
          const y = cy + Math.sin(ang) * p.r * 0.9;
          const tw = 0.4 + 0.6 * Math.abs(Math.sin(t * 1.5 + p.phase));
          ctx.beginPath();
          ctx.arc(x, y, p.size, 0, Math.PI * 2);
          ctx.fillStyle = p.color.replace(/,[\d.]+\)$/, `,${(tw * 0.9).toFixed(2)})`);
          ctx.fill();
        });
      }

      if (gc) {
        const ctx = gc.getContext("2d");
        ctx.clearRect(0, 0, gc.width, gc.height);
        const cx = gc.width / 2;
        const cy = gc.height / 2;
        const R = 34;
        const rot = t * 0.05;
        ctx.strokeStyle = geoStroke;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(cx, cy, R, 0, Math.PI * 2);
        ctx.stroke();
        for (let i = 0; i < 8; i++) {
          const ang = rot + i * ((Math.PI * 2) / 8);
          const dx = cx + Math.cos(ang) * R;
          const dy = cy + Math.sin(ang) * R;
          ctx.beginPath();
          ctx.arc(dx, dy, R * 0.5, 0, Math.PI * 2);
          ctx.stroke();
        }
      }

      rafRef.current = requestAnimationFrame(drawFrame);
    };

    rafRef.current = requestAnimationFrame(drawFrame);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const inner = Math.round(size * 1.3);

  return (
    <div className={`se-root ${className}`} style={{ width: size, height: size }} data-testid="sacred-emblem">
      <div className="se-aura" />
      <canvas ref={particlesRef} width={inner} height={inner} className="se-canvas" />
      <div className="se-ring se-ring-1" />
      <div className="se-ring se-ring-2" />
      <div className="se-ring se-ring-3" />
      <canvas ref={geometryRef} width={inner} height={inner} className="se-canvas" />
      <img
        src="/rewards/agent-eros-logo.png"
        alt="Agent Eros emblem"
        className="se-logo"
      />
    </div>
  );
};

export default SacredEmblem;
